import { useEffect, useMemo, useRef, useState } from 'react';
import { GoldButton } from '@/components/common/GoldButton';
import { MobileSpreadInfo } from '@/components/spread/MobileSpreadInfo';
import { PhotoUpload } from '@/components/common/PhotoUpload';
import { ModeSelector } from '@/components/spread/ModeSelector';
import { QuestionInput } from '@/components/spread/QuestionInput';
import { SpreadTypeGrid } from '@/components/spread/SpreadTypeGrid';
import { useReadingDraft } from '@/context/ReadingDraftContext';
import { useToast } from '@/hooks/useToast';
import { spreadTypes, t } from '@/i18n';
import type { SpreadMode, SpreadTypeId } from '@/types';
import { isPhotoTooLarge } from '@/utils/photoValidation';
import styles from './SpreadForm.module.css';

interface SpreadFormProps {
  initialMode?: SpreadMode;
  initialSpreadTypeId?: SpreadTypeId | null;
  submitting?: boolean;
  onSubmit: (payload: {
    mode: SpreadMode;
    spreadTypeId: SpreadTypeId;
    question: string;
    photo: File | null;
  }) => void;
}

function defaultTypeForMode(mode: SpreadMode): SpreadTypeId | null {
  if (mode === 'photo-reading') return 'auto-photo';
  return 'classic-3';
}

export function SpreadForm({
  onSubmit,
  initialMode,
  initialSpreadTypeId,
  submitting = false,
}: SpreadFormProps) {
  const { draft, setDraft } = useReadingDraft();
  // Mode always follows the props (i.e. the URL) — spread type, question and
  // photo are restored from the draft. This keeps a Home-page mode selection
  // always in charge of the mode, while still protecting the actual work
  // (what was picked/typed/attached) from an unrelated redirect. A restored
  // spreadTypeId that isn't valid for the resulting mode is harmless: the
  // `validType` fallback below just ignores it.
  const initialResolvedMode = initialMode ?? 'photo-reading';

  const [mode, setMode] = useState<SpreadMode>(initialResolvedMode);
  const [spreadTypeId, setSpreadTypeId] = useState<SpreadTypeId | null>(
    draft?.spreadTypeId ??
      initialSpreadTypeId ??
      defaultTypeForMode(initialResolvedMode),
  );
  const [question, setQuestion] = useState(draft?.question ?? '');
  const [photo, setPhoto] = useState<File | null>(draft?.photo ?? null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(() =>
    draft?.photo ? URL.createObjectURL(draft.photo) : null,
  );
  const { showToast } = useToast();

  const needsPhoto = mode === 'photo-reading' || mode === 'person-photo';

  // Keeps the in-memory draft current so a redirect at any point (not just
  // on submit failure) can restore exactly what was picked/typed/attached.
  useEffect(() => {
    setDraft({ spreadTypeId, question, photo });
  }, [spreadTypeId, question, photo, setDraft]);

  // Skip on the very first render: initial state above already seeded from
  // the restored draft (if any), and this effect's job is to reset the form
  // when `initialMode`/`initialSpreadTypeId` change *after* mount — running
  // it on mount too would immediately wipe a just-restored photo.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!initialMode) return;
    // Intentional: resets the form when the parent switches mode after mount.
    /* eslint-disable react-hooks/set-state-in-effect */
    setMode(initialMode);
    setSpreadTypeId(initialSpreadTypeId ?? defaultTypeForMode(initialMode));
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPhoto(null);
    setPreviewUrl(null);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [initialMode, initialSpreadTypeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const validType = useMemo(() => {
    const allowed = spreadTypes
      .filter((s) => s.modes.includes(mode))
      .map((s) => s.id);
    if (spreadTypeId && allowed.includes(spreadTypeId)) return spreadTypeId;
    return allowed[0] ?? null;
  }, [mode, spreadTypeId]);

  const onFile = (file: File | null) => {
    if (file && isPhotoTooLarge(file.size)) {
      showToast(t.photoUpload.tooLarge, 'error');
      return;
    }
    setPhoto(file);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(file ? URL.createObjectURL(file) : null);
  };

  const handleSubmit = () => {
    const id = validType;
    if (!id) return;
    if (needsPhoto && !photo) return;
    onSubmit({ mode, spreadTypeId: id, question, photo });
  };

  const disabled = !validType || (needsPhoto && !photo) || submitting;

  const handleModeChange = (next: SpreadMode) => {
    setMode(next);
    setSpreadTypeId(defaultTypeForMode(next));
  };

  return (
    <div>
      <ModeSelector value={mode} onChange={handleModeChange} />
      <MobileSpreadInfo />
      <SpreadTypeGrid
        mode={mode}
        value={validType}
        onChange={setSpreadTypeId}
      />
      <QuestionInput value={question} onChange={setQuestion} />
      {needsPhoto ? (
        <PhotoUpload previewUrl={previewUrl} onFile={onFile} />
      ) : null}
      <GoldButton
        className={styles.cta}
        onClick={handleSubmit}
        disabled={disabled}
      >
        {submitting ? t.common.sending : t.spread.submit}
      </GoldButton>
    </div>
  );
}
